export interface JobSearchBoardDefinition {
  id: string
  name: string
  domain: string
  description: string
  icon: string
  tier: number
}

export interface JobSearchBoard extends JobSearchBoardDefinition {
  selected: boolean
}

export interface JobSearchFilterOption {
  value: string
  label: string
}

export interface JobSearchFilters {
  keywords: string
  location: string
  seniority: string[]
  exclusions: string
  recencyDays: number | null
  jobType: string[]
  experience: string
}

export const WEB_JOB_SEARCH_SOURCE_ID = 'web'
export const WEB_JOB_SEARCH_DOMAIN = 'web'

export const DEFAULT_JOB_SEARCH_SOURCE_IDS = [WEB_JOB_SEARCH_SOURCE_ID]

export const JOB_SEARCH_SENIORITY_OPTIONS: JobSearchFilterOption[] = [
  { value: 'entry OR junior OR associate', label: 'Entry Level' },
  { value: 'mid OR intermediate OR 2-4 years', label: 'Mid Level' },
  { value: 'senior OR lead OR 5+ years', label: 'Senior Level' },
  { value: 'principal OR staff OR 7+ years', label: 'Principal/Staff' },
  { value: 'manager OR director OR VP', label: 'Management' },
]

export const JOB_SEARCH_JOB_TYPE_OPTIONS: JobSearchFilterOption[] = [
  { value: 'full-time', label: 'Full-time' },
  { value: 'part-time', label: 'Part-time' },
  { value: 'contract', label: 'Contract' },
  { value: 'internship', label: 'Internship' },
  { value: 'remote', label: 'Remote' },
]

export const JOB_SEARCH_TIER_NAMES: Record<number, string> = {
  0: 'General Search',
  1: 'Most Popular',
  2: 'Popular Job Boards',
  3: 'ATS Platforms',
  5: 'Company Careers',
}

export const JOB_SEARCH_BOARD_CATALOG: JobSearchBoardDefinition[] = [
  { id: 'web', name: 'Web', domain: 'web', description: 'General web search, not necessarily the same as Google', icon: '🌐', tier: 0 },
  { id: 'linkedin', name: 'LinkedIn', domain: 'linkedin.com/jobs', description: 'Professional network', icon: '💼', tier: 1 },
  { id: 'indeed', name: 'Indeed', domain: 'indeed.com', description: 'Job aggregator', icon: '🔍', tier: 1 },
  { id: 'glassdoor', name: 'Glassdoor', domain: 'glassdoor.com', description: 'Company reviews', icon: '🏢', tier: 1 },
  { id: 'ziprecruiter', name: 'ZipRecruiter', domain: 'ziprecruiter.com', description: 'AI-powered matching', icon: '⚡', tier: 1 },
  { id: 'monster', name: 'Monster', domain: 'monster.com', description: 'Career platform', icon: '👹', tier: 2 },
  { id: 'careerbuilder', name: 'CareerBuilder', domain: 'careerbuilder.com', description: 'Global job board', icon: '🌍', tier: 2 },
  { id: 'simplyhired', name: 'SimplyHired', domain: 'simplyhired.com', description: 'Job search engine', icon: '🎯', tier: 2 },
  { id: 'snagajob', name: 'Snagajob', domain: 'snagajob.com', description: 'Hourly jobs', icon: '⏰', tier: 2 },
  { id: 'flexjobs', name: 'FlexJobs', domain: 'flexjobs.com', description: 'Remote & flexible jobs', icon: '🏠', tier: 2 },
  { id: 'dice', name: 'Dice', domain: 'dice.com', description: 'Tech jobs', icon: '🎲', tier: 2 },
  { id: 'greenhouse', name: 'Greenhouse', domain: 'boards.greenhouse.io', description: 'ATS platform', icon: '🌱', tier: 3 },
  { id: 'lever', name: 'Lever', domain: 'jobs.lever.co', description: 'Modern ATS', icon: '⚖️', tier: 3 },
  { id: 'workday', name: 'Workday', domain: 'workday.com', description: 'Enterprise ATS', icon: '💼', tier: 3 },
  { id: 'smartrecruiters', name: 'SmartRecruiters', domain: 'smartrecruiters.com', description: 'Talent acquisition', icon: '🧠', tier: 3 },
  { id: 'workable', name: 'Workable', domain: 'workable.com', description: 'Recruiting software', icon: '⚙️', tier: 3 },
  { id: 'bamboohr', name: 'BambooHR', domain: 'bamboohr.com', description: 'HR software', icon: '🎋', tier: 3 },
  { id: 'jobvite', name: 'Jobvite', domain: 'jobvite.com', description: 'Recruiting platform', icon: '📧', tier: 3 },
  { id: 'icims', name: 'iCIMS', domain: 'icims.com', description: 'Talent cloud', icon: '☁️', tier: 3 },
  { id: 'bullhorn', name: 'Bullhorn', domain: 'bullhorn.com', description: 'Staffing software', icon: '🐂', tier: 3 },
  { id: 'jobscore', name: 'JobScore', domain: 'jobscore.com', description: 'Applicant tracking', icon: '⭐', tier: 3 },
  { id: 'recruiterbox', name: 'Recruiterbox', domain: 'recruiterbox.com', description: 'Hiring platform', icon: '📦', tier: 3 },
  { id: 'bamboohr_jobs', name: 'BambooHR Jobs', domain: 'bamboohr.com/careers', description: 'BambooHR careers', icon: '🎋', tier: 5 },
  { id: 'smartrecruiters_jobs', name: 'SmartRecruiters Jobs', domain: 'jobs.smartrecruiters.com', description: 'SmartRecruiters careers', icon: '🧠', tier: 5 },
  { id: 'jobvite_jobs', name: 'Jobvite Jobs', domain: 'jobs.jobvite.com', description: 'Jobvite careers', icon: '📧', tier: 5 },
  { id: 'icims_jobs', name: 'iCIMS Jobs', domain: 'careers.icims.com', description: 'iCIMS careers', icon: '☁️', tier: 5 },
  { id: 'bullhorn_jobs', name: 'Bullhorn Jobs', domain: 'jobs.bullhorn.com', description: 'Bullhorn careers', icon: '🐂', tier: 5 },
  { id: 'jobscore_jobs', name: 'JobScore Jobs', domain: 'jobs.jobscore.com', description: 'JobScore careers', icon: '⭐', tier: 5 },
  { id: 'recruiterbox_jobs', name: 'Recruiterbox Jobs', domain: 'jobs.recruiterbox.com', description: 'Recruiterbox careers', icon: '📦', tier: 5 },
  { id: 'workable_jobs', name: 'Workable Jobs', domain: 'jobs.workable.com', description: 'Workable careers', icon: '⚙️', tier: 5 },
  { id: 'latticehq', name: 'Lattice', domain: 'lattice.com/careers', description: 'Lattice careers', icon: '🧩', tier: 5 },
]

const sanitizeText = (value: unknown): string => {
  if (value === null || value === undefined) {
    return ''
  }
  return String(value).trim()
}

const normalizeJobSearchDomain = (value: string): string =>
  value
    .trim()
    .toLowerCase()
    .replace(/^https?:\/\//, '')
    .replace(/\/$/, '')

const escapeSearchTerm = (value: string): string => value.replace(/"/g, '\\"')

const formatBooleanSearchTerm = (value: string): string => {
  const sanitized = sanitizeText(value)
  if (!sanitized) {
    return ''
  }

  return /[^A-Za-z0-9._/-]/.test(sanitized)
    ? `"${escapeSearchTerm(sanitized)}"`
    : sanitized
}

const buildCommaSeparatedBooleanClause = (value: unknown): string => {
  const terms = sanitizeTextList(value)
  if (terms.length === 0) {
    return ''
  }
  if (terms.length === 1) {
    return terms[0]
  }
  return buildOrClause(terms, formatBooleanSearchTerm)
}

const sanitizeTextList = (value: unknown): string[] => {
  const rawValues = Array.isArray(value) ? value : value === null || value === undefined ? [] : [value]
  const values: string[] = []
  const seen = new Set<string>()

  rawValues.forEach((rawValue) => {
    String(rawValue)
      .split(',')
      .map((part) => part.trim())
      .filter(Boolean)
      .forEach((part) => {
        if (seen.has(part)) {
          return
        }
        seen.add(part)
        values.push(part)
      })
  })

  return values
}

const sanitizeRecencyDays = (value: unknown): number | null => {
  if (value === null || value === undefined || value === '') {
    return null
  }
  const parsed = Number(value)
  if (!Number.isFinite(parsed) || parsed <= 0) {
    return null
  }
  return Math.floor(parsed)
}

export const createDefaultJobSearchFilters = (): JobSearchFilters => ({
  keywords: '',
  location: '',
  seniority: [],
  exclusions: '',
  recencyDays: 7,
  jobType: [],
  experience: '',
})

export const hydrateJobSearchFilters = (value: unknown): JobSearchFilters => {
  const record = value && typeof value === 'object' ? (value as Record<string, unknown>) : {}
  return {
    keywords: sanitizeText(record.keywords),
    location: sanitizeText(record.location),
    seniority: sanitizeTextList(record.seniority),
    exclusions: sanitizeText(record.exclusions),
    recencyDays: sanitizeRecencyDays(record.recencyDays ?? record.recency) ?? 7,
    jobType: sanitizeTextList(record.jobType),
    experience: sanitizeText(record.experience),
  }
}

export const createJobSearchBoards = (
  selectedIds: string[] = DEFAULT_JOB_SEARCH_SOURCE_IDS,
): JobSearchBoard[] => {
  const selectedSet = new Set(selectedIds)
  return JOB_SEARCH_BOARD_CATALOG.map((board) => ({
    ...board,
    selected: selectedSet.has(board.id),
  }))
}

export const hydrateJobSearchBoards = (value: unknown): JobSearchBoard[] => {
  if (!Array.isArray(value)) {
    return createJobSearchBoards()
  }

  const selectedIds = value
    .map((item) => {
      if (typeof item === 'string') {
        return item
      }
      if (item && typeof item === 'object') {
        const board = item as Record<string, unknown>
        return board.selected ? sanitizeText(board.id) : ''
      }
      return ''
    })
    .filter(Boolean)

  return createJobSearchBoards(
    selectedIds.length > 0 ? selectedIds : DEFAULT_JOB_SEARCH_SOURCE_IDS,
  )
}

export const getSelectedJobSearchBoards = (
  boards: JobSearchBoard[],
): JobSearchBoard[] => boards.filter((board) => board.selected)

export const getSelectedJobSearchSourceIds = (boards: JobSearchBoard[]): string[] =>
  getSelectedJobSearchBoards(boards).map((board) => board.id)

export const getSelectedJobSearchDomains = (boards: JobSearchBoard[]): string[] =>
  getSelectedJobSearchBoards(boards).map((board) => board.domain)

export const getJobSearchBoardByDomain = (
  domain: string,
): JobSearchBoardDefinition | undefined => {
  const normalizedDomain = normalizeJobSearchDomain(domain)
  const normalizedHost = normalizedDomain.split('/')[0]

  return JOB_SEARCH_BOARD_CATALOG.find((board) => {
    const normalizedBoardDomain = normalizeJobSearchDomain(board.domain)
    const normalizedBoardHost = normalizedBoardDomain.split('/')[0]

    return (
      normalizedDomain === normalizedBoardDomain ||
      normalizedDomain === normalizedBoardHost ||
      normalizedHost === normalizedBoardDomain ||
      normalizedHost === normalizedBoardHost
    )
  })
}

export const getJobSearchSourceIdsForDomains = (domains: string[]): string[] => {
  const sourceIds: string[] = []
  const seen = new Set<string>()

  domains.forEach((domain) => {
    const board = getJobSearchBoardByDomain(domain)
    if (!board || seen.has(board.id)) {
      return
    }
    seen.add(board.id)
    sourceIds.push(board.id)
  })

  return sourceIds
}

export const enforceSingleJobSearchBoardSelection = (
  boards: JobSearchBoard[],
  boardId?: string,
): JobSearchBoard[] => {
  if (boards.length === 0) {
    return boards
  }

  const preferredBoard = boardId
    ? boards.find((board) => board.id === boardId)
    : boards.find((board) => board.selected) ?? boards[0]

  if (!preferredBoard) {
    return boards
  }

  return boards.map((board) => ({
    ...board,
    selected: board.id === preferredBoard.id,
  }))
}

export const buildJobSearchContext = (filters: JobSearchFilters): string => {
  const locationClause = buildCommaSeparatedBooleanClause(filters.location)
  const parts = [
    locationClause ? `Location: ${locationClause}` : '',
    filters.seniority.length > 0 ? `Seniority: ${filters.seniority.join(', ')}` : '',
    filters.jobType.length > 0 ? `Job Type: ${filters.jobType.join(', ')}` : '',
    filters.experience ? `Experience: ${filters.experience}` : '',
    filters.exclusions ? `Exclude: ${filters.exclusions}` : '',
  ]

  return parts.filter(Boolean).join('; ')
}

export const buildIntelligentSearchQuery = (keywords: string): string =>
  buildCommaSeparatedBooleanClause(keywords)

const normalizeNaturalLanguageTerm = (value: string): string =>
  sanitizeText(value).replace(/\s+OR\s+/gi, ' or ')

const buildNaturalLanguageClause = (value: unknown): string =>
  sanitizeTextList(value)
    .map((term) => normalizeNaturalLanguageTerm(term))
    .filter(Boolean)
    .join(' or ')

const buildOrClause = (
  values: string[],
  formatValue: (value: string) => string,
): string => {
  const clauses = values
    .map((value) => sanitizeText(value))
    .filter(Boolean)
    .map((value) => formatValue(value))

  if (clauses.length === 0) {
    return ''
  }

  if (clauses.length === 1) {
    return clauses[0]
  }

  return `(${clauses.join(' OR ')})`
}

export const buildGoogleJobSearchQuery = (
  filters: JobSearchFilters,
  domains: string[],
): string => {
  const queryParts: string[] = []
  const filteredDomains = domains.filter(
    (domain) => normalizeJobSearchDomain(domain) !== WEB_JOB_SEARCH_DOMAIN,
  )

  const keywordsClause = buildIntelligentSearchQuery(filters.keywords)
  if (keywordsClause) {
    queryParts.push(keywordsClause)
  }

  const seniorityClause = buildOrClause(
    filters.seniority,
    (value) => `(${value})`,
  )
  if (seniorityClause) {
    queryParts.push(seniorityClause)
  }

  const locationClause = buildOrClause(
    sanitizeTextList(filters.location),
    formatBooleanSearchTerm,
  )
  if (locationClause) {
    queryParts.push(locationClause)
  }

  const jobTypeClause = buildOrClause(
    filters.jobType,
    (value) => `"${value}"`,
  )
  if (jobTypeClause) {
    queryParts.push(jobTypeClause)
  }

  if (filters.experience) {
    queryParts.push(
      `(${filters.experience
        .split(',')
        .map((value) => value.trim())
        .join(' OR ')})`,
    )
  }

  if (filteredDomains.length > 0) {
    queryParts.push(filteredDomains.map((domain) => `site:${domain}`).join(' OR '))
  }

  if (filters.exclusions) {
    queryParts.push(
      filters.exclusions
        .split(',')
        .map((value) => `-"${value.trim()}"`)
        .join(' '),
    )
  }

  return queryParts.join(' ').trim()
}

export const buildTavilyJobSearchQuery = (
  filters: JobSearchFilters,
): string => {
  const keywords = buildNaturalLanguageClause(filters.keywords)
  const locationTerms = sanitizeTextList(filters.location).map((term) =>
    normalizeNaturalLanguageTerm(term),
  )
  const jobTypeTerms = filters.jobType.map((term) =>
    normalizeNaturalLanguageTerm(term),
  )
  const seniorityTerms = filters.seniority.map((term) =>
    normalizeNaturalLanguageTerm(term),
  )
  const exclusions = buildNaturalLanguageClause(filters.exclusions)
  const experience = normalizeNaturalLanguageTerm(filters.experience)

  const parts: string[] = []
  parts.push(keywords ? `${keywords} jobs` : 'jobs')

  if (jobTypeTerms.includes('remote') && !locationTerms.includes('remote')) {
    locationTerms.push('remote')
  }

  if (locationTerms.length > 0) {
    parts.push(`in ${locationTerms.join(' or ')}`)
  }

  const remainingJobTypes = jobTypeTerms.filter((term) => term !== 'remote')
  if (remainingJobTypes.length > 0) {
    parts.push(`for ${remainingJobTypes.join(' or ')} roles`)
  }

  if (seniorityTerms.length > 0) {
    parts.push(`at ${seniorityTerms.join(' or ')} level`)
  }

  if (experience) {
    parts.push(`requiring ${experience} experience`)
  }

  if (exclusions) {
    parts.push(`excluding ${exclusions}`)
  }

  return parts.join(' ').trim()
}
